import React, { useState, useRef, useEffect } from "react";
// Removed local JSON fallback to ensure data source is strictly backend+DDB
import { MultiPaneChart } from './components/Chart';
import { MultiPaneChartRef, TradeMarker } from './components/Chart/MultiPaneChart';
import { Strategy } from './components/StrategyBuilder/types';
import AnalysisTabs from './components/Analysis/AnalysisTabs';
import SignalManager from './components/SignalManager/SignalManager';
import RightSidebarPanel from './components/RightSidebarPanel';
import { SymbolSelector } from './components/SymbolSelector';
import { TimeframeSelector } from './components/TimeframeSelector';
import { Button, Space, Spin, Dropdown, Tooltip, Segmented, Card, message } from 'antd';
import { AreaChartOutlined, BookOutlined, LogoutOutlined, UserOutlined, GlobalOutlined, SunOutlined, MoonOutlined, StockOutlined, BorderOutlined, AimOutlined, ClearOutlined, BarChartOutlined } from '@ant-design/icons';
import { useAppTheme } from './theme/ThemeProvider';
import { useTranslation } from 'react-i18next';
// LanguageSwitcher component removed - using inline dropdown in nav
import { useBackendRealtimeData as useRealtimeData } from './hooks/useBackendRealtimeData';
import { loadConfig } from './utils/config';
import { configureAmplify, getCurrentUserInfo } from './utils/auth';
import { initializeAwsSdk, isAuthenticatedWithSDK, getCurrentUserWithSDK, signOutWithSDK, handleGoogleOAuthCallback, getUserInfoFromLocalToken } from './utils/auth-aws-sdk';
import './utils/auth-debug'; // 加载认证调试工具
import AuthModal from './components/AuthModal';
import StockDashboard from './components/StockDashboard/StockDashboard';
import { UsageIndicator } from './components/UsageIndicator';
import { useUsageContext } from './contexts/UsageContext';
import { QuestionnairePage } from './components/Questionnaire';
import { getQuestionnaireStatus, completeOnboarding } from './services/questionnaireService';
import { getUserIdentityForApi } from './utils/auth-aws-sdk';
import { logger } from './utils/logger';
import IndicatorPanel from './components/Indicators/IndicatorPanel';
import { INDICATOR_DEFAULT_PLACEMENT, IndicatorId, IndicatorPlacement } from './constants/indicators';
import UserProfile from './components/UserProfile/UserProfile';
import { OnboardingPage } from './components/Tutorial';
import IndicatorLimitModal from './components/Indicators/IndicatorLimitModal';
import { NotificationBell } from './components/NotificationBell';

const App: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { themeName, theme, setThemeName } = useAppTheme();
  const { usageStatus } = useUsageContext();
  const [currentPage, setCurrentPage] = useState<'analysis' | 'strategies' | 'profile'>('analysis');
  const [selectedAssetType, setSelectedAssetType] = useState<'crypto' | 'stocks'>('crypto');
  const [selectedSymbol, setSelectedSymbol] = useState<string>('BTCUSDT');
  const [selectedTimeframe, setSelectedTimeframe] = useState<string>('1h');
  const [selectedRect, setSelectedRect] = useState<any>(null);
  const [selectedPoints, setSelectedPoints] = useState<{ time: number; price: number }[]>([]);
  const [analysisActiveTabKey, setAnalysisActiveTabKey] = useState<'rectangle' | 'point' | 'similarPattern'>('rectangle');
  const [pointAnalysisAutoRunToken, setPointAnalysisAutoRunToken] = useState<number>(0);
  const [rectangleAnalysisAutoRunToken, setRectangleAnalysisAutoRunToken] = useState<number>(0);
  const [agentRectangleAnalysisResult, setAgentRectangleAnalysisResult] = useState<any>(null);
  const [pendingSaveData, setPendingSaveData] = useState<{
    analysisResult: any,
    timeRange: { start_time: string; end_time: string },
    selectedRectangle?: any,
    selectedPoint?: { time: number; price: number },
    symbol?: string,
    timeframe?: string
  } | null>(null);
  const [screenWidth, setScreenWidth] = useState<number>(window.innerWidth);
  // 工具激活状态（仅用于UI高亮反馈）
  const [isRectangleMode, setIsRectangleMode] = useState<boolean>(false);
  const [isPointMode, setIsPointMode] = useState<boolean>(false);

  // Real-time data hook
  const {
    data: realtimeData,
    isConnected,
    latestPrice,
    // lastUpdateTime,
    error: realtimeError,
    isLoading: isRealtimeLoading,
    updateSubscription,
    lastUpdateType,
    lastUpdatedCandle
  } = useRealtimeData({
    symbol: selectedSymbol,
    interval: selectedTimeframe,
    historicalLimit: 10000,
    fallbackData: []
  });
  const [configLoaded, setConfigLoaded] = useState(false);
  const [isAuthenticatedState, setIsAuthenticatedState] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);
  const [userUuid, setUserUuid] = useState<string>('');
  const [questionnaireLoading, setQuestionnaireLoading] = useState(false);
  const chartApiRef = useRef<MultiPaneChartRef | null>(null);
  const [isIndicatorPanelOpen, setIsIndicatorPanelOpen] = useState(false);
  const [selectedIndicators, setSelectedIndicators] = useState<IndicatorId[]>([]);
  const [indicatorPlacements, setIndicatorPlacements] = useState<
    Record<IndicatorId, 'main' | 'pane'>
  >(INDICATOR_DEFAULT_PLACEMENT);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [isFirstTimeOnboarding, setIsFirstTimeOnboarding] = useState(false); // 区分首次 Onboarding 和查看帮助
  const [isIndicatorUpsellVisible, setIsIndicatorUpsellVisible] = useState(false);
  const [indicatorLimitInfo, setIndicatorLimitInfo] = useState<{ selectedCount: number; limit: number }>({
    selectedCount: 0,
    limit: 2,
  });

  // 离开分析页或切换资产类型时，刷新工具与选择状态（适用于 Crypto 与 Stocks）
  const prevAssetTypeRef = useRef<'crypto' | 'stocks'>(selectedAssetType);
  useEffect(() => {
    const leftAnalysisPage = currentPage !== 'analysis';
    const assetTypeChanged = prevAssetTypeRef.current !== selectedAssetType;

    if (leftAnalysisPage || assetTypeChanged) {
      setIsRectangleMode(false);
      setIsPointMode(false);
      setSelectedRect(null);
      setSelectedPoints([]);
      chartApiRef.current?.clearRectangle?.();
      chartApiRef.current?.clearPoints?.();
    }

    prevAssetTypeRef.current = selectedAssetType;
  }, [currentPage, selectedAssetType]);

  // 当符号或时间框架变化时，更新订阅
  const prevSymbolRef = useRef(selectedSymbol);
  const prevTimeframeRef = useRef(selectedTimeframe);

  const handleIndicatorToggle = (indicatorId: IndicatorId) => {
    const planCode = usageStatus?.plan || 'free';
    const hasUnlimitedIndicators = ['pro', 'enterprise', 'premium'].includes(planCode);
    const freeLimit = 2;

    setSelectedIndicators((prev) => {
      if (prev.includes(indicatorId)) {
        return prev.filter((item) => item !== indicatorId);
      }

      if (!hasUnlimitedIndicators && prev.length >= freeLimit) {
        setIndicatorLimitInfo({ selectedCount: prev.length, limit: freeLimit });
        setIsIndicatorUpsellVisible(true);
        return prev;
      }

      return [...prev, indicatorId];
    });
  };

  const handleIndicatorPlacementChange = (indicatorId: IndicatorId, placement: IndicatorPlacement) => {
    setIndicatorPlacements((prev) => ({
      ...prev,
      [indicatorId]: placement,
    }));
  };

  // Agent frontend tool: atomically set indicators and open the indicator panel
  const setIndicators = (args: {
    selected?: IndicatorId[];
    mode?: 'replace' | 'add' | 'remove';
    placements?: Partial<Record<IndicatorId, IndicatorPlacement>>;
    open_panel?: boolean;
  }): { success: boolean; result?: any; error?: string } => {
    const mode = args.mode || 'replace';
    const requested = Array.isArray(args.selected) ? args.selected : [];
    const openPanel = args.open_panel !== false; // default true

    const planCode = usageStatus?.plan || 'free';
    const hasUnlimitedIndicators = ['pro', 'enterprise', 'premium'].includes(planCode);
    const freeLimit = 2;

    const enforceLimit = (ids: IndicatorId[]) => {
      if (hasUnlimitedIndicators) return ids;
      if (ids.length <= freeLimit) return ids;
      setIndicatorLimitInfo({ selectedCount: ids.length, limit: freeLimit });
      setIsIndicatorUpsellVisible(true);
      // Keep it deterministic: take the first N requested
      return ids.slice(0, freeLimit);
    };

    setSelectedIndicators((prev) => {
      let next: IndicatorId[] = prev;
      if (mode === 'replace') {
        next = [...requested];
      } else if (mode === 'add') {
        const set = new Set<IndicatorId>(prev);
        requested.forEach((id) => set.add(id));
        next = Array.from(set);
      } else if (mode === 'remove') {
        const remove = new Set<IndicatorId>(requested);
        next = prev.filter((id) => !remove.has(id));
      }
      return enforceLimit(next);
    });

    if (args.placements) {
      setIndicatorPlacements((prev) => ({ ...prev, ...args.placements }));
    }

    if (openPanel) setIsIndicatorPanelOpen(true);
    return { success: true, result: { selected: requested, mode, open_panel: openPanel } };
  };

  useEffect(() => {
    if (selectedAssetType === 'crypto') {
      // Only update if symbol or timeframe actually changed
      const symbolChanged = prevSymbolRef.current !== selectedSymbol;
      const timeframeChanged = prevTimeframeRef.current !== selectedTimeframe;

      if (symbolChanged || timeframeChanged) {
        logger.log(`Symbol/Timeframe changed: ${prevSymbolRef.current}@${prevTimeframeRef.current} -> ${selectedSymbol}@${selectedTimeframe}`);

        // Add a small delay to prevent rapid switching issues
        const timeoutId = setTimeout(() => {
          updateSubscription(selectedSymbol, selectedTimeframe);
        }, 100);

        // Update refs
        prevSymbolRef.current = selectedSymbol;
        prevTimeframeRef.current = selectedTimeframe;

        return () => clearTimeout(timeoutId);
      }
    }
  }, [selectedSymbol, selectedTimeframe, selectedAssetType, updateSubscription]);

  useEffect(() => {
    const initializeApp = async () => {
      try {
        logger.log('开始加载配置...');
        const config = await loadConfig();
        logger.log('配置加载成功:', config);

        // 配置AWS Amplify
        logger.log('开始配置AWS Amplify...');
        configureAmplify(config);
        logger.log('AWS Amplify配置完成');

        // 初始化AWS SDK
        logger.log('开始初始化AWS SDK...');
        await initializeAwsSdk();
        logger.log('AWS SDK初始化完成');

        setConfigLoaded(true);

        // 检查是否是OAuth回调
        const urlParams = new URLSearchParams(window.location.search);
        logger.log('检查URL参数:', {
          code: urlParams.get('code'),
          state: urlParams.get('state'),
          fullUrl: window.location.href,
          search: window.location.search
        });

        // 检查OAuth错误
        if (urlParams.get('error')) {
          const error = urlParams.get('error');
          const errorDescription = urlParams.get('error_description');
          const state = urlParams.get('state');

          logger.error('OAuth错误:', { error, errorDescription, state });

          // 清除URL参数
          window.history.replaceState({}, document.title, window.location.pathname);

          // 显示错误信息
          if (error === 'invalid_scope') {
            logger.error('OAuth scope无效，请检查scope配置');
          } else if (error === 'invalid_request') {
            logger.error('OAuth请求无效，请检查请求参数');
          }

          return;
        }

        if (urlParams.get('code') && urlParams.get('state')) {
          logger.log('检测到OAuth回调参数:', { code: urlParams.get('code'), state: urlParams.get('state') });

          // 检查是否已经处理过这个授权码（防止重复处理）
          const processedCode = sessionStorage.getItem('processed_oauth_code');
          const currentCode = urlParams.get('code');

          if (processedCode === currentCode) {
            logger.log('授权码已被处理过，跳过重复处理');
            // 清除URL参数
            window.history.replaceState({}, document.title, window.location.pathname);
          return;
          }

          // 检查是否有其他OAuth处理正在进行
          const oauthProcessing = sessionStorage.getItem('oauth_processing');
          if (oauthProcessing === 'true') {
            logger.log('OAuth处理正在进行中，跳过重复处理');
            // 清除URL参数
            window.history.replaceState({}, document.title, window.location.pathname);
            return;
          }

          // 标记OAuth处理开始
          sessionStorage.setItem('oauth_processing', 'true');
          sessionStorage.setItem('processed_oauth_code', currentCode!);

          // 使用AWS SDK处理OAuth回调
          const callbackResult = await handleGoogleOAuthCallback(
            urlParams.get('code')!,
            urlParams.get('state')!
          );
          logger.log('OAuth回调处理结果:', callbackResult);

          if (callbackResult.success) {
            logger.log('OAuth回调成功，设置认证状态');

            // 等待一小段时间确保token已保存到localStorage
            await new Promise(resolve => setTimeout(resolve, 100));

            // 验证token是否已正确保存
            const tokenCheck = isAuthenticatedWithSDK();

            if (tokenCheck) {
              setIsAuthenticatedState(true);
              try {
                const currentUser = await getCurrentUserWithSDK();
                setUser(currentUser);
                logger.log('用户信息获取成功:', currentUser);
              } catch (error) {
                logger.log('无法获取用户信息，但认证状态已设置:', error);
              }

              // 获取用户UUID并检查问卷状态
              const userIdentity = getUserIdentityForApi();
              if (userIdentity && userIdentity.sub) {
                logger.log('OAuth回调获取到用户UUID:', userIdentity.sub);
                await checkQuestionnaireStatus(userIdentity.sub);
              } else {
                logger.error('OAuth回调无法获取用户UUID');
              }
            } else {
              logger.error('Token保存失败，认证状态设置失败');
              // 如果token保存失败，清除处理标记但不设置认证状态
            }

            // 清除URL参数和OAuth处理标记
            window.history.replaceState({}, document.title, window.location.pathname);
            sessionStorage.removeItem('processed_oauth_code');
            sessionStorage.removeItem('oauth_processing');

            // 如果认证成功，刷新页面
            if (tokenCheck) {
              window.location.reload();
              return;
            }
          } else {
            logger.log('OAuth回调失败:', callbackResult.error);
            // 清除URL参数和OAuth处理标记
            window.history.replaceState({}, document.title, window.location.pathname);
            sessionStorage.removeItem('processed_oauth_code');
            sessionStorage.removeItem('oauth_processing');

            // 如果是OAuth错误，显示更友好的错误信息
            if (callbackResult.error) {
              if (callbackResult.error.includes('invalid_grant')) {
                logger.error('授权码无效，可能的原因:');
                logger.error('1. 授权码已过期（通常10分钟有效期）');
                logger.error('2. 授权码已被使用过');
                logger.error('3. redirect_uri不匹配');
                logger.error('4. 客户端密钥错误');
                logger.error('请重新尝试登录');
              } else if (callbackResult.error.includes('invalid_request')) {
                logger.error('请求无效，可能的原因:');
                logger.error('1. 请求参数格式错误');
                logger.error('2. 缺少必需的参数');
                logger.error('3. 参数值无效');
                logger.error('4. 客户端配置错误');
                logger.error('请检查配置并重新尝试登录');
              }
            }
          }
        }

        // 检查用户是否已通过AWS SDK登录
        const authenticated = isAuthenticatedWithSDK();
        logger.log('AWS SDK认证状态:', authenticated);

        // 输出详细的认证调试信息
        if (typeof window !== 'undefined' && (window as any).debugAuth) {
          logger.log('=== 应用启动时的认证状态 ===');
          (window as any).debugAuth.debugAuthState();
        }

        setIsAuthenticatedState(authenticated);

        if (authenticated) {
          // 先尝试从本地Token加载用户信息（快速响应）
          const localUser = getUserInfoFromLocalToken();
          if (localUser) {
            logger.log('从本地Token加载用户信息:', localUser);
            setUser(localUser);
          }

          // 然后异步从服务器获取最新信息
          try {
            const currentUser = await getCurrentUserWithSDK();
            if (currentUser) {
              setUser(currentUser);
              logger.log('用户信息加载成功:', currentUser);
            } else {
              // 如果获取失败（例如Token过期），提示用户
              logger.warn('无法获取最新用户信息，Token可能已过期');
            }

            // 获取用户UUID并检查问卷状态
            const userIdentity = getUserIdentityForApi();
            if (userIdentity && userIdentity.sub) {
              logger.log('应用启动时获取到用户UUID:', userIdentity.sub);
              await checkQuestionnaireStatus(userIdentity.sub);
            } else {
              logger.error('应用启动时无法获取用户UUID');
            }
          } catch (error) {
            logger.log('无法获取用户信息:', error);
            // 如果无法获取用户信息，可能是token过期，清除认证状态
            logger.log('认证状态已清除');
            setIsAuthenticatedState(false);
            setUser(null);
          }
        }
      } catch (error) {
        logger.error('Failed to load config:', error);
        setConfigLoaded(true);
      }
    };

    initializeApp();
  }, []);

  // Handle Stripe billing redirect: show Account Settings when upgrade succeeds
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const billingSuccess = urlParams.get('billing_success');
    const billingCancel = urlParams.get('billing_cancel');

    if (billingSuccess === 'true') {
      // Jump to Account Settings page
      setCurrentPage('profile');
      // Friendly toast
      const msg =
        i18n.language === 'zh-CN'
          ? '订阅已升级为 Pro，今日配额已更新'
          : 'Subscription upgraded to Pro successfully.';
      message.success(msg);
    } else if (billingCancel === 'true') {
      const msg =
        i18n.language === 'zh-CN'
          ? '已取消结账，未完成订阅升级'
          : 'Checkout was cancelled.';
      message.info(msg);
    }

    if (billingSuccess || billingCancel) {
      urlParams.delete('billing_success');
      urlParams.delete('billing_cancel');
      const newSearch = urlParams.toString();
      const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '');
      window.history.replaceState({}, document.title, newUrl);
    }
  }, [i18n.language]);

  // 监听屏幕尺寸变化
  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 检查问卷状态
  const checkQuestionnaireStatus = async (uuid: string) => {
    try {
      setQuestionnaireLoading(true);
      const status = await getQuestionnaireStatus(uuid);
      logger.log('问卷状态:', status);
      
      if (!status.is_completed) {
        // 问卷未完成，显示问卷页面
        setShowQuestionnaire(true);
        setUserUuid(uuid);
      } else if (!status.onboarding_completed) {
        // 问卷已完成但 Onboarding 未完成，显示 Onboarding
        logger.log('用户已完成问卷，但 Onboarding 未完成，显示 Onboarding');
        setUserUuid(uuid);
        setIsFirstTimeOnboarding(true);
        setShowOnboarding(true);
      } else {
        logger.log('用户已完成问卷和 Onboarding，直接进入主界面');
      }
    } catch (error: any) {
      logger.error('检查问卷状态失败:', error);
      
      // 检查是否是认证错误（401或403）
      const statusCode = error?.status || (error?.message?.match(/\b(401|403)\b/)?.[0]);
      const isAuthError = statusCode === 401 || 
                         statusCode === 403 ||
                         error?.message?.includes('Unauthorized') ||
                         error?.message?.includes('Forbidden');
      
      if (isAuthError) {
        logger.warn('认证失败，可能是token过期，清除认证状态');
        // 清除认证状态，让用户重新登录
        setIsAuthenticatedState(false);
        setUser(null);
        setShowQuestionnaire(false);
        setUserUuid('');
        // 清除过期的token
        localStorage.removeItem('cognito_access_token');
        localStorage.removeItem('cognito_id_token');
        localStorage.removeItem('cognito_refresh_token');
        return;
      }
      
      // 如果不是认证错误，可能是其他原因（如网络错误），仍然显示问卷页面
      setShowQuestionnaire(true);
      setUserUuid(uuid);
    } finally {
      setQuestionnaireLoading(false);
    }
  };

  const handleAuthSuccess = async () => {
    logger.log('=== 认证成功回调被调用 ===');

    // 由于我们使用AWS SDK直接登录，直接设置认证状态
    setIsAuthenticatedState(true);

    // 尝试获取用户信息（如果AWS SDK登录成功，我们可以从登录结果中获取）
    try {
      const currentUser = await getCurrentUserInfo();
      setUser(currentUser);
      logger.log('用户信息设置成功:', currentUser);
    } catch (error) {
      logger.log('无法获取用户信息，但认证状态已设置');
      // 即使无法获取用户信息，也保持认证状态
    }

    // 获取用户UUID并检查问卷状态
    const userIdentity = getUserIdentityForApi();
    if (userIdentity && userIdentity.sub) {
      logger.log('获取到用户UUID:', userIdentity.sub);
      await checkQuestionnaireStatus(userIdentity.sub);
    } else {
      logger.error('无法获取用户UUID');
      // 如果无法获取UUID，直接进入主界面
    }

    // 登录成功后刷新页面
    window.location.reload();
  };

  const handleLogout = async () => {
    await signOutWithSDK();
    setIsAuthenticatedState(false);
    setUser(null);
    setShowQuestionnaire(false);
    setUserUuid('');
  };

  const handleQuestionnaireComplete = () => {
    logger.log('问卷完成，显示 Onboarding');
    setShowQuestionnaire(false);
    // 问卷完成后显示 Onboarding（首次）
    setIsFirstTimeOnboarding(true);
    setShowOnboarding(true);
  };

  const handleOnboardingComplete = async () => {
    logger.log('Onboarding 完成');
    // 只有首次 Onboarding 时才调用 API 更新状态
    if (isFirstTimeOnboarding && userUuid) {
      try {
        await completeOnboarding(userUuid);
        logger.log('Onboarding 状态已更新到数据库');
      } catch (error) {
        logger.error('更新 Onboarding 状态失败:', error);
      }
    }
    setShowOnboarding(false);
    setIsFirstTimeOnboarding(false);
    // 完成教程后跳转到 Chart Analysis 页面
    setCurrentPage('analysis');
  };

  // 打开帮助页面（非首次，只是查看）
  const handleOpenHelp = () => {
    setIsFirstTimeOnboarding(false);
    setShowOnboarding(true);
  };

  // 响应式断点
  const isLargeScreen = screenWidth >= 1024;
  const isTabletScreen = screenWidth >= 768;

  // 语言切换函数
  const handleLanguageChange = (language: string) => {
    i18n.changeLanguage(language);
    localStorage.setItem('language', language);
  };

  // 处理资产选择变化
  const handleSymbolChange = (symbol: string) => {
    // Prevent switching if already loading
    if (isRealtimeLoading) {
      logger.log('Symbol switch blocked - already loading data');
      return;
    }

    setSelectedSymbol(symbol);

    // 根据资产类型设置默认时间框架（所有 USDT 加密默认 1h）
    if (symbol.endsWith('USDT')) {
      setSelectedTimeframe('1h');
    }

    // 清除当前选择状态和工具模式
    setSelectedRect(null);
    setSelectedPoints([]);
    setIsRectangleMode(false);
    setIsPointMode(false);
    chartApiRef.current?.clearRectangle?.();
    chartApiRef.current?.clearPoints?.();
  };

  // Handle strategy selection - show B/S markers on chart
  const handleStrategySelect = (strategy: Strategy | null) => {
    if (!strategy || !strategy.backtest_results?.trades) {
      // Clear markers when no strategy selected
      chartApiRef.current?.clearTradeMarkers?.();
      return;
    }

    // Create markers for each trade (entry and exit)
    const markers: TradeMarker[] = [];
    
    strategy.backtest_results.trades.forEach((trade) => {
      // Entry marker (buy)
      markers.push({
        time: Math.floor(trade.entry_time / 1000), // Convert ms to seconds
        type: 'buy',
        price: trade.entry_price,
        label: 'B'
      });
      
      // Exit marker (sell)
      markers.push({
        time: Math.floor(trade.exit_time / 1000), // Convert ms to seconds
        type: 'sell',
        price: trade.exit_price,
        label: 'S'
      });
    });

    // Sort markers by time
    markers.sort((a, b) => a.time - b.time);
    
    // Set markers on chart
    chartApiRef.current?.setTradeMarkers?.(markers);
  };

  // 处理时间框架变化
  const handleTimeframeChange = (timeframe: string) => {
    // 验证时间框架是否对当前资产可用
    const isTimeframeAvailable = (symbol: string, timeframe: string) => {
      if (symbol.endsWith('USDT')) {
        return ['1m', '5m', '30m', '1h', '4h', '1d'].includes(timeframe);
      }
      return false;
    };

    if (isTimeframeAvailable(selectedSymbol, timeframe)) {
      setSelectedTimeframe(timeframe);
      // 清除当前选择状态和工具模式
      setSelectedRect(null);
      setSelectedPoints([]);
      setIsRectangleMode(false);
      setIsPointMode(false);
      chartApiRef.current?.clearRectangle?.();
      chartApiRef.current?.clearPoints?.();
    }
  };

  // Agent frontend tool: switch chart context in one atomic action (asset type / symbol / timeframe)
  const setChartContext = (args: {
    asset_type?: 'crypto' | 'stocks';
    symbol?: string;
    timeframe?: string;
    normalize?: boolean;
    reason?: string;
  }): { success: boolean; result?: { asset_type: 'crypto' | 'stocks'; symbol: string; timeframe: string }; error?: string } => {
    // Prevent switching if already loading
    if (isRealtimeLoading) {
      return { success: false, error: 'Chart is loading. Please try again in a moment.' };
    }

    const normalize = args.normalize !== false;
    const nextAssetType: 'crypto' | 'stocks' = args.asset_type ?? selectedAssetType;

    // Determine target symbol/timeframe using provided args or current values
    let nextSymbol = args.symbol ?? selectedSymbol;
    let nextTimeframe = args.timeframe ?? selectedTimeframe;

    if (normalize) {
      if (typeof nextSymbol === 'string') nextSymbol = nextSymbol.trim().toUpperCase();
      if (typeof nextTimeframe === 'string') nextTimeframe = nextTimeframe.trim().toLowerCase();

      // Crypto shorthand normalization: BTC -> BTCUSDT
      if (nextAssetType === 'crypto' && nextSymbol && !nextSymbol.endsWith('USDT') && /^[A-Z]{2,10}$/.test(nextSymbol)) {
        nextSymbol = `${nextSymbol}USDT`;
      }
    }

    // Basic validation
    if (nextAssetType === 'crypto') {
      const allowed = ['1m', '5m', '30m', '1h', '4h', '1d'];
      if (!nextSymbol.endsWith('USDT')) {
        return { success: false, error: `Unsupported crypto symbol: ${nextSymbol}` };
      }
      if (nextTimeframe && !allowed.includes(nextTimeframe)) {
        return { success: false, error: `Unsupported timeframe: ${nextTimeframe}. Allowed: ${allowed.join(', ')}` };
      }
    }

    // Apply changes
    const assetTypeChanged = nextAssetType !== selectedAssetType;
    const symbolChanged = nextSymbol !== selectedSymbol;
    const timeframeChanged = nextTimeframe !== selectedTimeframe;

    if (!assetTypeChanged && !symbolChanged && !timeframeChanged) {
      return { success: true, result: { asset_type: selectedAssetType, symbol: selectedSymbol, timeframe: selectedTimeframe } };
    }

    // Change asset type first (if needed)
    if (assetTypeChanged) {
      setSelectedAssetType(nextAssetType);
    }

    // Apply symbol + timeframe without relying on async state ordering
    if (symbolChanged) {
      setSelectedSymbol(nextSymbol);
    }

    if (timeframeChanged) {
      setSelectedTimeframe(nextTimeframe);
    } else if (symbolChanged && nextAssetType === 'crypto' && nextSymbol.endsWith('USDT') && !args.timeframe) {
      // If only symbol is set for crypto and user didn't specify timeframe, keep existing UX default (1h).
      setSelectedTimeframe('1h');
      nextTimeframe = '1h';
    }

    // Clear selections + tool modes (same as manual switching)
    setSelectedRect(null);
    setSelectedPoints([]);
    setIsRectangleMode(false);
    setIsPointMode(false);
    chartApiRef.current?.clearRectangle?.();
    chartApiRef.current?.clearPoints?.();

    return { success: true, result: { asset_type: nextAssetType, symbol: nextSymbol, timeframe: nextTimeframe } };
  };

  if (!configLoaded) {
    return <div>加载配置中...</div>;
  }

  // 如果未登录，显示认证模态框
  if (!isAuthenticatedState) {
    return (
      <AuthModal
        visible={true}
        onSuccess={handleAuthSuccess}
      />
    );
  }

  // 如果需要显示问卷页面
  if (showQuestionnaire) {
    if (questionnaireLoading) {
      return (
        <div style={{
          minHeight: '100vh',
          background: theme.appBackground,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Spin size="large" />
        </div>
      );
    }
    
    return (
      <QuestionnairePage
        onComplete={handleQuestionnaireComplete}
        userUuid={userUuid}
      />
    );
  }

  // 如果需要显示 Onboarding 入门引导页面（首次登录）
  if (showOnboarding) {
    return (
      <OnboardingPage onComplete={handleOnboardingComplete} />
    );
  }


  // 已移除未使用的相似分析与矩形转换辅助函数

  // 处理保存信号
  const handleSaveSignal = (analysisResult: any, context: any) => {
    // context 可能来自矩形分析（selectedRectangle），也可能来自点分析（timeRange, selectedPoint）
    let timeRange: { start_time: string; end_time: string } | null = null;

    if (context?.timeRange?.start_time && context?.timeRange?.end_time) {
      timeRange = context.timeRange;
    } else if (context?.startTime && context?.endTime) {
      // 矩形对象
      timeRange = {
        start_time: context.startTime,
        end_time: context.endTime
      };
    }

    if (!timeRange) {
      logger.warn('保存信号缺少时间范围');
      return;
    }

    setPendingSaveData({
      analysisResult,
      timeRange,
      selectedRectangle: context?.startTime ? context : undefined,
      selectedPoint: context?.selectedPoint,
      // 优先使用context中传递的symbol和timeframe（股票模式下会传递正确的股票代码）
      // 如果没有，则使用全局的selectedSymbol和selectedTimeframe
      symbol: context?.symbol || selectedSymbol,
      timeframe: context?.timeframe || selectedTimeframe
    });
    setCurrentPage('strategies');
  };

  const effectivePlanCode = usageStatus?.plan || 'free';
  const currentPlanLabel =
    effectivePlanCode === 'pro'
      ? 'Pro Trader'
      : effectivePlanCode === 'enterprise' || effectivePlanCode === 'premium'
        ? 'Enterprise'
        : 'Starter';
  const recommendedPlanLabel = i18n.language === 'zh-CN' ? 'Pro Trader 专业版' : 'Pro Trader';

  const handleIndicatorUpgrade = () => {
    setIsIndicatorUpsellVisible(false);
    setIsIndicatorPanelOpen(false);
    setCurrentPage('profile');
    message.info(
      i18n.language === 'zh-CN'
        ? '已为你打开套餐页面，选择 Pro Trader 以解锁全部指标'
        : 'Opening your plan page. Choose Pro Trader to unlock all indicators.'
    );
  };

  return (
    <>
    <div style={{ minHeight: "100vh", background: theme.appBackground, color: theme.textPrimary, display: 'flex', flexDirection: 'column' }}>
      <style>
        {`
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.5; }
            100% { opacity: 1; }
          }
        `}
      </style>
      {/* 导航栏 */}
      <div style={{
        background: theme.surfaceBackground,
        borderBottom: `1px solid ${theme.surfaceBorder}`,
        padding: screenWidth < 768 ? "16px 16px" : "20px 48px",
        position: "sticky",
        top: 0,
        zIndex: 100,
        display: "grid",
        gridTemplateColumns: screenWidth >= 768 ? "1fr auto 1fr" : "auto 1fr",
        alignItems: "center",
        gap: '16px'
      }}>
        {/* Left: Logo */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '1px',
          flexShrink: 0
        }}>
          {/* Logo Image - 根据主题切换 */}
          <img 
            src={themeName === 'dark' ? '/logo-dark.png' : '/logo-light.png'} 
            alt="Alphora AI" 
            style={{ 
              width: '36px', 
              height: '36px',
              objectFit: 'contain'
            }} 
          />
          <span style={{
            fontSize: '18px',
            fontWeight: 600,
            color: theme.textPrimary,
            letterSpacing: '-0.02em'
          }}>
            Alphora AI
          </span>
        </div>

        {/* Center: Navigation Links */}
        {screenWidth >= 768 && (
          <nav style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            gap: '80px'
          }}>
            <span
              onClick={() => setCurrentPage('analysis')}
              style={{
                fontSize: '15px',
                fontWeight: 500,
                color: currentPage === 'analysis' ? theme.textPrimary : theme.textSecondary,
                cursor: 'pointer'
              }}
            >
              {t('nav.chartAnalysis')}
            </span>
            <span
              onClick={() => setCurrentPage('strategies')}
              style={{
                fontSize: '15px',
                fontWeight: 500,
                color: currentPage === 'strategies' ? theme.textPrimary : theme.textSecondary,
                cursor: 'pointer'
              }}
            >
              {t('nav.signalManagement')}
            </span>
            <span
              onClick={handleOpenHelp}
              style={{
                fontSize: '15px',
                fontWeight: 500,
                color: theme.textSecondary,
                cursor: 'pointer'
              }}
            >
              {t('nav.tutorial')}
            </span>
          </nav>
        )}

        {/* Right: Status & Icons */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'flex-end',
          gap: screenWidth < 768 ? '8px' : '16px',
          flexShrink: 0
        }}>
          {/* System Status Pill */}
          <Tooltip
            title={isConnected
              ? t('realtime.connectedTooltip', { symbol: selectedSymbol, interval: selectedTimeframe, price: latestPrice?.toLocaleString?.() || '' })
              : (realtimeError || t('realtime.disconnected'))
            }
            placement="bottom"
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 14px',
              borderRadius: '20px',
              background: isConnected 
                ? (themeName === 'dark' ? 'rgba(34, 197, 94, 0.15)' : 'rgba(82, 196, 26, 0.1)')
                : (themeName === 'dark' ? 'rgba(239, 68, 68, 0.15)' : 'rgba(255, 77, 79, 0.1)'),
              border: `1px solid ${isConnected 
                ? (themeName === 'dark' ? 'rgba(34, 197, 94, 0.3)' : 'rgba(82, 196, 26, 0.3)')
                : (themeName === 'dark' ? 'rgba(239, 68, 68, 0.3)' : 'rgba(255, 77, 79, 0.3)')}`,
              cursor: 'default'
            }}>
              <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: isConnected ? theme.accentPrimary : '#ff4d4f',
                boxShadow: isConnected 
                  ? `0 0 8px ${theme.accentPrimary}` 
                  : '0 0 8px #ff4d4f'
              }} />
              {screenWidth >= 640 && (
                <span style={{
                  fontSize: '13px',
                  fontWeight: 500,
                  color: isConnected ? theme.accentPrimary : '#ff4d4f',
                  whiteSpace: 'nowrap'
                }}>
                  {isConnected ? t('nav.systemOnline') : t('nav.systemOffline')}
                </span>
              )}
            </div>
          </Tooltip>

          {/* Icon Group */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            padding: '4px 8px',
            borderRadius: '24px',
            background: themeName === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'
          }}>
            {/* Language Dropdown */}
            <Dropdown
              menu={{
                items: [
                  {
                    key: 'zh-CN',
                    label: '中文',
                    onClick: () => handleLanguageChange('zh-CN')
                  },
                  {
                    key: 'en-US',
                    label: 'English',
                    onClick: () => handleLanguageChange('en-US')
                  }
                ]
              }}
              placement="bottomRight"
            >
              <Button
                type="text"
                icon={<GlobalOutlined style={{ fontSize: '18px' }} />}
                style={{ 
                  color: theme.textSecondary,
                  width: '36px',
                  height: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              />
            </Dropdown>

            {/* Theme Toggle */}
            <Button
              type="text"
              icon={themeName === 'dark' ? <SunOutlined style={{ fontSize: '18px' }} /> : <MoonOutlined style={{ fontSize: '18px' }} />}
              onClick={() => setThemeName(themeName === 'dark' ? 'light' : 'dark')}
              style={{ 
                color: theme.textSecondary,
                width: '36px',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            />

            {/* Usage Indicator */}
            <div style={{
              width: '36px',
              height: '36px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <UsageIndicator size="small" />
            </div>
          </div>

          {/* User Menu */}
          <NotificationBell />
          <Dropdown
            menu={{
              items: [
                ...(screenWidth < 768 ? [
                  {
                    key: 'analysis',
                    icon: <AreaChartOutlined />,
                    label: t('nav.chartAnalysis'),
                    onClick: () => setCurrentPage('analysis')
                  },
                  {
                    key: 'signals',
                    icon: <BookOutlined />,
                    label: t('nav.strategyManagement'),
                    onClick: () => setCurrentPage('strategies')
                  },
                  { type: 'divider' as const },
                ] : []),
                {
                  key: 'profile',
                  icon: <UserOutlined />,
                  label: 'Account Settings',
                  onClick: () => setCurrentPage('profile')
                },
                {
                  key: 'logout',
                  icon: <LogoutOutlined />,
                  label: t('auth.signOut'),
                  onClick: handleLogout
                }
              ]
            }}
            placement="bottomRight"
          >
            <Button
              type="text"
              icon={<UserOutlined style={{ fontSize: '18px' }} />}
              style={{ 
                color: theme.textSecondary,
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: `1px solid ${theme.surfaceBorder}`
              }}
            />
          </Dropdown>
        </div>
      </div>

      {/* 页面内容 */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      {currentPage === 'profile' ? (
        <div style={{ background: theme.surfaceBackground, flex: 1 }}>
          <UserProfile user={user} theme={theme} />
        </div>
      ) : currentPage === 'analysis' ? (
        <div style={{ background: theme.surfaceBackground }}>
          {/* Asset Type Selector */}
          <div style={{
            padding: '16px 20px',
            borderBottom: `1px solid ${theme.surfaceBorder}`,
            background: theme.surfaceBackground,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '16px'
          }}>
            <Segmented
              size="large"
              value={selectedAssetType}
              onChange={(val) => {
                setSelectedAssetType(val as 'crypto' | 'stocks');
                // Scroll to top when switching asset types
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              options={[
                {
                  label: (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <AreaChartOutlined />
                      <span>{t('asset.crypto')}</span>
                    </div>
                  ),
                  value: 'crypto'
                },
                {
                  label: (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <StockOutlined />
                      <span>{t('asset.stocks')}</span>
                    </div>
                  ),
                  value: 'stocks'
                }
              ]}
              style={{
                background: theme.surfaceBackground,
                border: `1px solid ${theme.surfaceBorder}`
              }}
            />
          </div>

          {/* 主内容区域 - 根据资产类型显示不同内容 */}
          {selectedAssetType === 'crypto' ? (
            <div style={{
              width: '100%',
              maxWidth: '100vw',
              margin: '0',
              padding: '16px 24px', // 与股票界面一致的留白
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              overflow: 'hidden'
            }}>
              {/* 资产选择器 */}
              {/* 上方：图表和聊天区域 */}
              <div style={{
                display: 'flex',
                flexDirection: isLargeScreen ? 'row' : 'column',
                gap: isLargeScreen ? '16px' : '8px',
                minHeight: 'auto',
                width: '100%',
                boxSizing: 'border-box'
              }}>
                {/* 图表区域（卡片风格，与股票一致） */}
                <div style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column'
                }}>
        <Card
          title={
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%'
              }}>
                <Space align="center" size="middle">
                  <SymbolSelector
                    selectedSymbol={selectedSymbol}
                    onSymbolChange={handleSymbolChange}
                    size="middle"
                  />
                  <span style={{ color: theme.textPrimary, fontSize: '16px' }}>
                    @ {selectedTimeframe}
                  </span>
                  <Button
                    icon={<BarChartOutlined />}
                    onClick={() => setIsIndicatorPanelOpen(true)}
                  >
                    {t('indicator.button')}
                  </Button>
                </Space>
                <Space size="small">
                  <TimeframeSelector
                    symbol={selectedSymbol}
                    selectedTimeframe={selectedTimeframe}
                    onTimeframeChange={handleTimeframeChange}
                  />
                </Space>
              </div>
            }
                    style={{
                      background: theme.surfaceBackground,
                      borderColor: theme.surfaceBorder,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                    styles={{
                      body: {
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        padding: 0
                      }
                    }}
                  >
                    {isRealtimeLoading ? (
                      <div style={{ textAlign: 'center', padding: '40px 0' }}>
                        <Spin size="large" />
                        <div style={{ marginTop: 16, color: theme.textPrimary }}>
                          {t('common.loading')}...
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: '8px', width: '100%', height: '100%' }}>
                        {/* 工具栏（与股票尺寸一致，保留crypto工具逻辑） */}
                        <div style={{
                          width: '55px',
                          background: theme.surfaceBackground,
                          border: `1px solid ${theme.surfaceBorder}`,
                          borderRadius: '8px',
                          padding: '8px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '6px',
                          height: 'auto',
                          minHeight: '500px',
                          alignItems: 'center'
                        }}>
                          <div style={{
                            fontSize: '10px',
                            fontWeight: 600,
                            color: theme.textPrimary,
                            marginBottom: '8px',
                            textAlign: 'center'
                          }}>
                            Tools
                          </div>

                          <Tooltip title={isRectangleMode ? t('tools.exitRectangleTool') : t('tools.rectangleTool')}>
                            <Button
                              type={isRectangleMode ? 'primary' : 'default'}
                              icon={<BorderOutlined />}
                              size="small"
                              onClick={() => {
                                chartApiRef.current?.toggleRectangleTool();
                                setIsRectangleMode(prev => {
                                  const next = !prev;
                                  if (next) setIsPointMode(false);
                                  return next;
                                });
                              }}
                              style={{
                                width: '32px',
                                height: '32px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            />
                          </Tooltip>

                          <Tooltip title={isPointMode ? t('tools.exitPointTool') : t('tools.pointTool')}>
                            <Button
                              type={isPointMode ? 'primary' : 'default'}
                              icon={<AimOutlined />}
                              size="small"
                              onClick={() => {
                                chartApiRef.current?.togglePointTool();
                                setIsPointMode(prev => {
                                  const next = !prev;
                                  if (next) setIsRectangleMode(false);
                                  return next;
                                });
                              }}
                              style={{
                                width: '32px',
                                height: '32px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            />
                          </Tooltip>

                          {selectedRect && (
                            <Tooltip title={t('tools.removeRectangle')}>
                              <Button
                                icon={<ClearOutlined />}
                                size="small"
                                onClick={() => {
                                  chartApiRef.current?.clearRectangle();
                                }}
                                style={{
                                  width: '32px',
                                  height: '32px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              />
                            </Tooltip>
                          )}

                          {selectedPoints && selectedPoints.length > 0 && (
                            <Tooltip title={t('tools.clearPoints')}>
                              <Button
                                icon={<ClearOutlined />}
                                size="small"
                                onClick={() => {
                                  chartApiRef.current?.clearPoints();
                                }}
                                style={{
                                  width: '32px',
                                  height: '32px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              />
                            </Tooltip>
                          )}

                        </div>

                        {/* 图表区域 */}
                        <div style={{ flex: 1 }}>
                          <MultiPaneChart
                            key={`${selectedSymbol}-${selectedTimeframe}`}
                            ref={chartApiRef}
                            ohlcData={realtimeData}
                            onRectChange={(rect) => {
                              setSelectedRect(rect);
                              // Keep toolbar button state consistent with agent-driven rectangle selection
                              if (rect) {
                                setIsRectangleMode(true);
                                setIsPointMode(false);
                              }
                            }}
                            onPointsChange={setSelectedPoints}
                            lastUpdateType={lastUpdateType}
                            lastUpdatedCandle={lastUpdatedCandle}
                            width={0}
                            mainHeight={isLargeScreen ? 700 : 500}
                            subHeight={isLargeScreen ? 180 : 150}
                            hideToolbar={true}
                            showVolume={selectedIndicators.includes('volume')}
                            showRsi={selectedIndicators.includes('rsi')}
                            showMacd={selectedIndicators.includes('macd')}
                            showSma={selectedIndicators.includes('sma')}
                            showEma={selectedIndicators.includes('ema')}
                            showWma={selectedIndicators.includes('wma')}
                            showWema={selectedIndicators.includes('wema')}
                            showTypicalPrice={selectedIndicators.includes('typicalPrice')}
                            showObv={selectedIndicators.includes('obv')}
                            showBollingerBands={selectedIndicators.includes('bollingerBands')}
                            showWilliamsR={selectedIndicators.includes('williamsR')}
                            showAtr={selectedIndicators.includes('atr')}
                            showMfi={selectedIndicators.includes('mfi')}
                            showStochastic={selectedIndicators.includes('stochastic')}
                            showRoc={selectedIndicators.includes('roc')}
                            showTrix={selectedIndicators.includes('trix')}
                            showVwap={selectedIndicators.includes('vwap')}
                            showAdl={selectedIndicators.includes('adl')}
                            showAdx={selectedIndicators.includes('adx')}
                            showAo={selectedIndicators.includes('ao')}
                            showCci={selectedIndicators.includes('cci')}
                            showForceIndex={selectedIndicators.includes('forceIndex')}
                            showKst={selectedIndicators.includes('kst')}
                            showPsar={selectedIndicators.includes('psar')}
                            showStochRsi={selectedIndicators.includes('stochRsi')}
                            showVolumeProfile={selectedIndicators.includes('volumeProfile')}
                            showIchimokuCloud={selectedIndicators.includes('ichimokuCloud')}
                            indicatorPlacements={indicatorPlacements}
                            onIndicatorToggle={handleIndicatorToggle}
                            onPlacementChange={handleIndicatorPlacementChange}
                          />
                        </div>
                      </div>
                    )}
                  </Card>
                </div>

                {/* 策略助手区域 - 完全响应式 */}
                {isTabletScreen && (  // 只在平板及以上显示
                  <div style={{
                    width: isLargeScreen
                      ? 'min(400px, 35vw)'      // 大屏幕：动态宽度，最大400px
                      : '100%',                 // 小屏幕：占满宽度
                    minWidth: isLargeScreen ? '320px' : 'auto',
                    height: isLargeScreen ? '770px' : '400px',
                    flexShrink: 0,
                    boxSizing: 'border-box'
                  }}>
                    <RightSidebarPanel
                      onSignalSelect={(signal) => {
                        logger.log('Selected signal:', signal);
                      }}
                      onStrategySelect={handleStrategySelect}
                      onTimeSelect={(timestamp) => {
                        chartApiRef.current?.scrollToTime?.(timestamp);
                      }}
                      // Agent 需要的 props
                      chartApiRef={chartApiRef}
                      ohlcData={realtimeData}
                      currentSymbol={selectedSymbol}
                      currentTimeframe={selectedTimeframe}
                      selectedPoint={selectedPoints && selectedPoints.length > 0 ? selectedPoints[0] : null}
                      onPointSelect={(point) => {
                        if (point) {
                          setSelectedPoints([{ time: point.time, price: point.price }]);
                          // Agent 选点：同步左侧工具栏状态，让“点工具”按钮高亮（与用户手动点击一致）
                          setIsPointMode(true);
                          setIsRectangleMode(false);
                        } else {
                          setSelectedPoints([]);
                        }
                      }}
                      onShowAnalysisPanel={(panel) => {
                        // 切换到对应分析 Tab（等价于用户点击 Tab）
                        setAnalysisActiveTabKey(panel === 'point' ? 'point' : 'rectangle');
                        // 滚动到分析面板
                        const analysisEl = document.querySelector('.ant-tabs');
                        if (analysisEl) {
                          analysisEl.scrollIntoView({ behavior: 'smooth' });
                        }
                      }}
                      onPointAnalysisAutoRun={() => {
                        setPointAnalysisAutoRunToken((prev) => prev + 1);
                      }}
                      onRectangleAnalysisAutoRun={() => {
                        setRectangleAnalysisAutoRunToken((prev) => prev + 1);
                      }}
                      onSetIndicators={(args) => setIndicators(args as any)}
                      onSetChartContext={(args) => setChartContext(args)}
                      onRectangleAnalysisResult={(result) => {
                        setAgentRectangleAnalysisResult(result);
                      }}
                      onCloseIndicatorPanel={() => setIsIndicatorPanelOpen(false)}
                    />
                  </div>
                )}
              </div>

              {/* 下方：分析面板跨越整个宽度 */}
              {!isRealtimeLoading && (
                <div style={{ width: '100%' }}>
                  <AnalysisTabs
                    ohlcData={realtimeData}
                    selectedRectangle={selectedRect}
                    selectedPoint={selectedPoints && selectedPoints.length > 0 ? selectedPoints[0] : undefined}
                    chartApiRef={chartApiRef}
                    onSaveSignal={handleSaveSignal}
                    activeTabKey={analysisActiveTabKey}
                    onActiveTabKeyChange={setAnalysisActiveTabKey}
                    pointAnalysisAutoRunToken={pointAnalysisAutoRunToken}
                    rectangleAnalysisAutoRunToken={rectangleAnalysisAutoRunToken}
                    agentRectangleAnalysisResult={agentRectangleAnalysisResult}
                  />
                </div>
              )}
            </div>
          ) : (
            /* 股票仪表板 */
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {/* Stock Dashboard */}
              <StockDashboard
                onSaveSignal={handleSaveSignal}
                selectedIndicators={selectedIndicators}
                onIndicatorToggle={handleIndicatorToggle}
                onOpenIndicatorPanel={() => setIsIndicatorPanelOpen(true)}
                indicatorPlacements={indicatorPlacements}
                onPlacementChange={handleIndicatorPlacementChange}
              />
            </div>
          )}


        </div>
      ) : (
        <SignalManager
          onSignalSelect={(signal) => {
            logger.log('Selected signal:', signal);
          }}
          onAnalysisResultSelect={() => {
            setCurrentPage('analysis');
          }}
          pendingSaveData={pendingSaveData}
          onSaveComplete={() => {
            setPendingSaveData(null);
            setCurrentPage('analysis');
          }}
        />
      )}
      </div>

      {/* 页脚 Footer */}
      <div style={{
        background: theme.surfaceBackground,
        borderTop: `1px solid ${theme.surfaceBorder}`,
        padding: '16px',
        textAlign: 'center',
        color: theme.textSecondary,
        fontSize: '16px'
      }}>
        © 2025 Alphora AI. All rights reserved.
      </div>
    </div>
      <IndicatorPanel
        open={isIndicatorPanelOpen}
        onClose={() => setIsIndicatorPanelOpen(false)}
        selectedIndicators={selectedIndicators}
        onToggleIndicator={handleIndicatorToggle}
        indicatorPlacements={indicatorPlacements}
        onPlacementChange={handleIndicatorPlacementChange}
        theme={theme}
      />
      <IndicatorLimitModal
        open={isIndicatorUpsellVisible}
        onClose={() => setIsIndicatorUpsellVisible(false)}
        onUpgrade={handleIndicatorUpgrade}
        currentPlanLabel={currentPlanLabel}
        recommendedPlanLabel={recommendedPlanLabel}
        currentLimit={indicatorLimitInfo.limit}
        selectedCount={indicatorLimitInfo.selectedCount}
      />
    </>
  );
};

export default App;
